import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AutopayMethodsScreen } from './AutopayMethodsScreen'
import { LoadingSpinner } from '../../../../components/LoadingSpinner'
import { useInstalledUpiIntents } from '../../../../hooks/mf-execution/useInstalledUpiIntents'
import { fetchMandateDetails } from '../../../../repository/mfExecution'
import { AutopayMethodSelection } from '../../../../types/mf-execution.types'
import {
  getBankAccountsFromDetails,
  getMandateAccountsFromDetails,
  reconcileAutopayMethodSelection,
  resolveAutopayPaymentMode,
  UPI_AUTO_PAY_MAX_LIMIT,
} from '../../../../utils/mf-execution/autopayMethodSelection'
import { parseAmountFromQueryParam } from '../../../../utils/mf-execution/paymentAmount'

type SetupAutopayMethodsLocationState = {
  selectedAutopayMethod?: AutopayMethodSelection | null
}

/**
 * Route wrapper for `AutopayMethodsScreen`.
 * Loads mandate-details (`bankAccounts` + `availableMandates`) and restores selection from navigation state.
 */
export default function SetupAutopayMethodsScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const sxpId = searchParams.get('sxpId') ?? ''
  const amount = parseAmountFromQueryParam(searchParams)
  const { installedUpiApps, requestUpiIntents } = useInstalledUpiIntents()

  const initialSelection = (location.state as SetupAutopayMethodsLocationState | null)?.selectedAutopayMethod

  const {
    data: mandateDetails,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['mandateDetails', sxpId],
    queryFn: () => fetchMandateDetails(sxpId),
    enabled: !!sxpId,
  })

  const mandateAccounts = useMemo(() => getMandateAccountsFromDetails(mandateDetails), [mandateDetails])
  const bankAccounts = useMemo(() => getBankAccountsFromDetails(mandateDetails), [mandateDetails])
  const requiredPaymentMode = resolveAutopayPaymentMode({
    mandateType: mandateDetails?.mandateType,
    amount,
  })

  const [selectedAutopayMethod, setSelectedAutopayMethod] = useState<AutopayMethodSelection | null>(
    () => initialSelection ?? null
  )

  useEffect(() => {
    if (!bankAccounts.length) return

    const reconciled = reconcileAutopayMethodSelection({
      selection: selectedAutopayMethod ?? initialSelection,
      mandateAccounts,
      bankAccounts,
      installedUpiApps,
      requiredMandateType: mandateDetails?.mandateType,
      amount,
    })

    if (!reconciled) return

    const hasSelectionChanged =
      selectedAutopayMethod?.accountId !== reconciled.accountId ||
      selectedAutopayMethod?.mode !== reconciled.mode ||
      selectedAutopayMethod?.upiPackageName !== reconciled.upiPackageName

    if (hasSelectionChanged) {
      setSelectedAutopayMethod(reconciled)
    }
  }, [
    amount,
    mandateAccounts,
    bankAccounts,
    installedUpiApps,
    initialSelection,
    mandateDetails?.mandateType,
    selectedAutopayMethod,
  ])

  useEffect(() => {
    if (!sxpId) {
      navigate(-1)
    }
  }, [sxpId, navigate])

  if (!sxpId) {
    return null
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (isError || !bankAccounts.length) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen bg-white p-6'>
        <p className='text-[#64748b] text-center text-[16px]'>Unable to load autopay methods. Please try again.</p>
      </div>
    )
  }

  return (
    <AutopayMethodsScreen
      amount={amount}
      upiAutoPayMaxLimit={mandateDetails?.maxLimit ?? UPI_AUTO_PAY_MAX_LIMIT}
      requiredPaymentMode={requiredPaymentMode}
      mandateAccounts={mandateAccounts}
      bankAccounts={bankAccounts}
      selectedAutopayMethod={selectedAutopayMethod}
      onAutopayMethodChange={setSelectedAutopayMethod}
      installedUpiApps={installedUpiApps}
      requestUpiIntents={requestUpiIntents}
      onConfirm={(selection) => {
        // Explicit path + query string: relative `navigate('..')` drops sxpId and breaks SetupAutoPay.
        navigate(`/v2/sip/auto-pay/methods?${searchParams.toString()}`, {
          state: { selectedAutopayMethod: selection },
        })
      }}
    />
  )
}
